import { z } from 'zod';
import {
  DIGITAL_FOOTPRINT_REPORT_STATES,
  MERCHANT_REPORT_RISK_LEVELS,
  MERCHANT_REPORT_STATUSES,
  MERCHANT_REPORT_TYPES,
  RISK_INDICATOR_RISK_LEVELS,
} from '../consts';

export const RiskLevelEnum = z.enum(['critical', 'warning', 'positive']);

export const SocialPageStatusEnum = z.enum(['detected', 'missing', 'unverified']);

export const FacebookPageSchema = z.object({
  id: z.string().nullish(),
  status: SocialPageStatusEnum.nullish().optional(),
  url: z.string().nullable(),
  name: z.string().nullish(),
  email: z.string().nullish(),
  likes: z.number().nullish(),
  address: z.string().nullish(),
  categories: z.array(z.string()).nullish(),
  phoneNumber: z.string().nullish(),
  creationDate: z.string().nullish(),
  screenshotUrl: z.string().url().nullish(),
});

export const InstagramPageSchema = z.object({
  id: z.string().nullish(),
  status: SocialPageStatusEnum.nullish().optional(),
  url: z.string().nullable(),
  username: z.string().nullish(),
  biography: z.string().nullish(),
  followers: z.number().nullish(),
  categories: z.array(z.string()).nullish(),
  isVerified: z.boolean().nullish(),
  screenshotUrl: z.string().url().nullish(),
  isBusinessProfile: z.boolean().nullish(),
});

export const SocialPage = z.object({
  url: z.string().nullable(),
  status: SocialPageStatusEnum.nullish().optional(),
});

export const MccSuggestionSchema = z.object({
  mcc: z.string(),
  description: z.string().nullish(),
  reasoning: z.string().nullish(),
});

export const MerchantCategoryCodeSummarySchema = z.object({
  primary: MccSuggestionSchema.nullish(),
  alternatives: z.array(MccSuggestionSchema).max(2).default([]),
});

export type MccSuggestion = z.infer<typeof MccSuggestionSchema>;
export type MerchantCategoryCodeSummary = z.infer<typeof MerchantCategoryCodeSummarySchema>;

export const MerchantDetailsSchema = z.object({
  email: z.string().nullish(),
  address: z.string().nullish(),
  country: z.string().nullish(),
  website: z.string().nullish(),
  industry: z.string().nullish(),
  companyName: z.string().nullish(),
  countryCode: z.string().nullish(),
  foundedDate: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  registrationNumber: z.string().nullish(),
  associatedPersons: z.array(z.string()).nullish(),
});

export const TagLevelEnum = z.enum(['positive', 'moderate', 'negative']);
export const TagSchema = z.object({
  name: z.string(),
  level: TagLevelEnum,
});

export const RiskTypeStatusSchema = z.object({
  status: z.enum(['alert', 'clear']),
});

export const RiskTypesSchema = z.object({
  legal: RiskTypeStatusSchema.nullish(),
  chargebacks: RiskTypeStatusSchema.nullish(),
  reputational: RiskTypeStatusSchema.nullish(),
  contentFindings: RiskTypeStatusSchema.nullish(),
  transactionLaundering: RiskTypeStatusSchema.nullish(),
});

// TODO: use df-schemas once admin is zod@v4 instead of duplicating schemas
/*
This is duplicated in libs/schemas/digital-footprint/src/aup.ts
because we can't use df-schemas with admin.

Please keep in sync when changing
 */
export const POLICY_FIT_STATUS_MAP = {
  NEUTRAL: 'neutral',
  RESTRICTED: 'restricted',
  PROHIBITED: 'prohibited',
  UNVERIFIED: 'unverified',
} as const;

export const POLICY_FIT_STATUSES = Object.values(POLICY_FIT_STATUS_MAP);

export const PolicyFitStatus = z.enum(['neutral', 'restricted', 'prohibited', 'unverified']);

export const PolicyFitSchema = z.object({
  status: PolicyFitStatus,
  displayName: z.string().optional(),
  version: z.coerce.number().optional(),
  description: z.string(),
});

export type PolicyFit = z.infer<typeof PolicyFitSchema>;
export type PolicyFitStatus = z.infer<typeof PolicyFitStatus>;

const ServerLocationSchema = z.object({
  ip: z.string().nullish(),
  country: z.string().nullish(),
  region: z.string().nullish(),
  city: z.string().nullish(),
  lat: z.number().nullish(),
  lng: z.number().nullish(),
  postalCode: z.string().nullish(),
  timezone: z.string().nullish(),
  geonameId: z.number().nullish(),
});

export const DomainInfoSchema = z.object({
  createdAt: z.string().nullish(),
  expiresAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
  hostedCount: z.number().nullish(),
  serverLocation: ServerLocationSchema.nullish(),
});

export const RiskIndicatorSchema = z
  .object({
    id: z.string(),
    name: z.string().nullish(),
    sourceUrl: z.string().nullish(),
    screenshot: z
      .object({
        screenshotUrl: z.string().url().nullish(),
      })
      .nullish(),
    explanation: z.string().nullish(),
    reason: z.string().nullish(),
    quoteFromSource: z.string().nullish(),
    riskLevel: z.enum(RISK_INDICATOR_RISK_LEVELS).nullish(),
    pageContext: z.string().nullish().optional(),
    status: SocialPageStatusEnum.nullish().optional(),
  })
  .passthrough();

export const NewEcosystemRecordSchema = z.object({
  domain: z.string(),
  relationship: z.string(),
  nodes: z.array(
    z.object({
      type: z.string(),
      value: z.string(),
    }),
  ),
});

export const LegacyEcosystemRecordSchema = z.object({
  domain: z.string(),
  relatedNode: z.string(),
  relatedNodeType: z.string(),
});

export const EcosystemRecordSchema = z.union([
  NewEcosystemRecordSchema,
  LegacyEcosystemRecordSchema,
]);

export const HighRiskVerticalAnalysisSectionSchema = z
  .object({
    displayName: z.string(),
    status: z.enum(['clear', 'alert']),
    content: z.string(),
  })
  .strict();

export const HighRiskVerticalAnalysisResultSchema = z
  .object({
    vertical: z.string(),
    promptName: z.string(),
    verticalDisplayName: z.string().optional(),
    summary: z.string().optional(),
    sections: z.array(HighRiskVerticalAnalysisSectionSchema).optional(),
  })
  .strict();

export const OngoingMonitoringChangeSchema = z.object({
  category: z.string(),
  reasoning: z.string(),
  severity: z.enum(['low', 'medium', 'high']),
});

export type OngoingMonitoringChange = z.infer<typeof OngoingMonitoringChangeSchema>;
export type OngoingMonitoringChanges = OngoingMonitoringChange[];

export const PreviousReportsSchema = z.object({
  previousMonitoringReportId: z.string().nullish(),
  onboardingReportId: z.string().nullish(),
});

export type PreviousReports = z.infer<typeof PreviousReportsSchema>;

// TODO: Deduplicate with libs/schemas/digital-footprint/src/company-registry-check.ts
// These schemas are duplicated here because the frontend cannot use @ballerine/digital-footprint-schemas
// due to Zod v3 vs v4 compatibility issues. Once frontend is migrated to Zod v4, remove this duplication
// and import from @ballerine/digital-footprint-schemas instead.
/**
 * Schema for company registry data
 * Represents structured data returned from company registry vendors (OpenCorporates, Kyckr, etc.)
 *
 * NOTE: This is a copy of CompanyRegistryDataSchema from @ballerine/digital-footprint-schemas
 */
export const CompanyRegistryDataSchema = z.object({
  name: z.string(),
  companyNumber: z.string(),
  jurisdictionCode: z.string(),
  incorporationDate: z.string().nullable(),
  dissolutionDate: z.string().nullable(),
  companyType: z.string().nullable(),
  registryUrl: z.string().nullable(),
  openCorporatesUrl: z.string().nullable().optional(),
  isBranch: z.boolean(),
  branchStatus: z.string().nullable(),
  isInactive: z.boolean(),
  currentStatus: z.string().nullable(),
  source: z.object({
    publisher: z.string(),
    url: z.string(),
    retrievedAt: z.string(),
  }),
  agentName: z.string().nullable(),
  alternativeNames: z.array(
    z.object({
      companyName: z.string(),
      type: z.string().nullable(),
      startDate: z.string().nullable(),
      endDate: z.string().nullable(),
      language: z.string().nullable(),
    }),
  ),
  previousNames: z.array(
    z.object({
      companyName: z.string(),
      type: z.string().nullable(),
      startDate: z.string().nullable(),
      endDate: z.string().nullable(),
      language: z.string().nullable(),
    }),
  ),
  numberOfEmployees: z.number().nullable(),
  registeredAddressInFull: z.string().nullable(),
  registeredAddress: z
    .object({
      country: z.string(),
      locality: z.string().nullable(),
      postalCode: z.string().nullable(),
      region: z.string().nullable(),
      streetAddress: z.string(),
    })
    .nullable(),
  industries: z.array(
    z.object({
      code: z.string(),
      description: z.string().nullable(),
      codeSchemeId: z.string(),
      codeSchemeName: z.string(),
      uid: z.string(),
    }),
  ),
  identifiers: z.array(
    z.object({
      uid: z.string(),
      identifierSystemCode: z.string(),
      identifierSystemName: z.string(),
    }),
  ),
  officers: z.array(
    z.object({
      name: z.string(),
      position: z.string().nullable(),
      startDate: z.string().nullable(),
      endDate: z.string().nullable(),
      occupation: z.string().nullable(),
      isInactive: z.boolean(),
      currentStatus: z.string().nullable(),
      address: z.string().nullable(),
    }),
  ),
});

/**
 * Union schema for company registry check results
 * Success is determined by the boolean 'success' field
 *
 * NOTE: This is a copy of CompanyRegistryCheckResultSchema from @ballerine/digital-footprint-schemas
 */
export const CompanyRegistryCheckResultSchema = z.object({
  success: z.boolean(),
  data: CompanyRegistryDataSchema.optional(),
  error: z.string().optional(),
  source: z.string(),
  jurisdictionCode: z.string(),
  retrievedAt: z.string(),
});

export type CompanyRegistryData = z.infer<typeof CompanyRegistryDataSchema>;
export type CompanyRegistryCheckResult = z.infer<typeof CompanyRegistryCheckResultSchema>;

/**
 * Stakeholder schema for UBOs and Directors
 */
export const StakeholderSchema = z.object({
  name: z.string(),
  role: z.enum(['ubo', 'director']),
});

export type Stakeholder = z.infer<typeof StakeholderSchema>;

/**
 * Transaction analysis input schema
 */
export const TransactionAnalysisSchema = z
  .object({
    enabled: z.boolean().nullish(),
    fileUrl: z.string().nullish(),
  })
  .nullish();

export type TransactionAnalysis = z.infer<typeof TransactionAnalysisSchema>;

export const DeclaredDetailsSchema = z
  .object({
    address: z.string().nullish(),
    mcc: z.string().nullish(),
    dba: z.string().nullish(),
    additionalUrls: z.array(z.string()).nullish(),
    addresses: z.array(z.string()).nullish(),
    emails: z.array(z.string()).nullish(),
    phoneNumbers: z.array(z.string()).nullish(),
    registrationNumber: z.string().nullish(),
    billingDescriptor: z.string().nullish(),
    additionalInformation: z.string().nullish(),
    stakeholders: z.array(StakeholderSchema).nullish(),
    companyRegistryCheck: z
      .object({
        enabled: z.boolean().nullish(),
        registrationNumber: z.string().nullish(),
        country: z.string().nullish(),
        state: z.string().nullish(),
      })
      .nullish(),
    transactionAnalysis: TransactionAnalysisSchema,
  })
  .nullish();

export type DeclaredDetails = z.infer<typeof DeclaredDetailsSchema>;

const CredentialsSchema = z.object({
  username: z.string().min(1),
  password: z.string().min(1),
});

export const GenerateReportInputSchema = z.object({
  websiteUrl: z.string().url(),
  companyName: z.string().optional(),
  merchantId: z.string().optional(),
  countryCode: z.string().max(2).optional(),
  businessId: z.string().optional(),
  businessCorrelationId: z.string().optional(),
  isOngoingMonitoringEnabled: z.boolean(),
  reportType: z.enum([MERCHANT_REPORT_TYPES[0], ...MERCHANT_REPORT_TYPES.slice(1)]),
  declaredDetails: DeclaredDetailsSchema.optional(),
  credentials: CredentialsSchema.optional(),
  msaFile: z.custom<File | undefined>(val => val === undefined || val instanceof File).optional(),
  transactionAnalysisFile: z
    .custom<File | undefined>(val => val === undefined || val instanceof File)
    .optional(),
});

export type GenerateReportInput = z.infer<typeof GenerateReportInputSchema>;

export const ReportSchema = z
  .object({
    id: z.string(),
    reportType: z.enum([MERCHANT_REPORT_TYPES[0], ...MERCHANT_REPORT_TYPES.slice(1)]),
    createdAt: z
      .string()
      .datetime()
      .transform(value => new Date(value)),
    updatedAt: z
      .string()
      .datetime()
      .transform(value => new Date(value)),
    displayDate: z
      .string()
      .datetime()
      .transform(value => new Date(value)),
    publishedAt: z
      .string()
      .datetime()
      .nullish()
      .transform(value => (value ? new Date(value) : null)),
    status: z.enum([MERCHANT_REPORT_STATUSES[0], ...MERCHANT_REPORT_STATUSES.slice(1)]),
    state: z.enum(DIGITAL_FOOTPRINT_REPORT_STATES).nullish(),
    monitoringStatus: z.boolean().nullish(),
    website: z.object({
      id: z.string(),
      url: z.string(),
      unsubscribedMonitoringAt: z.string().datetime().nullish(),
    }),
    customer: z.object({
      id: z.string(),
      displayName: z.string(),
      ongoingMonitoringEnabled: z.boolean(),
    }),
    business: z.object({
      id: z.string(),
      correlationId: z.string().nullish(),
    }),
    metadata: z.record(z.string(), z.unknown()).nullish(),
    companyName: z.string().nullish(),
    riskLevel: z.enum(MERCHANT_REPORT_RISK_LEVELS).nullish(),
    isAlert: z.boolean().nullish(),
    previousReports: PreviousReportsSchema.nullish(),
    declaredDetails: DeclaredDetailsSchema,
    data: z
      .object({
        lineOfBusiness: z.string().nullish(),
        companyName: z.string().nullish(),
        mcc: MerchantCategoryCodeSummarySchema.nullish(),
        bounceRate: z.string().nullish(),
        timeOnSite: z.string().nullish(),
        pagesPerVisit: z.string().nullish(),
        trafficSources: z.record(z.string(), z.number()).nullish(),
        visitorsCountries: z
          .object({
            data: z.record(z.string(), z.number()).nullish(),
            startDate: z.string().nullish(),
            endDate: z.string().nullish(),
          })
          .nullish(),
        monthlyVisits: z.record(z.string(), z.number()).nullish(),
        facebookPage: FacebookPageSchema.nullish(),
        instagramPage: InstagramPageSchema.nullish(),
        linkedinPage: SocialPage.nullish(),
        tiktokPage: SocialPage.nullish(),
        twitterPage: SocialPage.nullish(),
        merchantDetails: MerchantDetailsSchema.nullish(),
        tags: z.array(TagSchema).nullish(),
        references: z.string().nullish(),
        riskTypes: RiskTypesSchema.nullish(),
        policyFit: PolicyFitSchema.nullish(),
        domainInfo: DomainInfoSchema.nullish(),
        trafficRiskIndicators: z.array(RiskIndicatorSchema).nullish(),
        companyReputationRiskIndicators: z.array(RiskIndicatorSchema).nullish(),
        websiteReputationRiskIndicators: z.array(RiskIndicatorSchema).nullish(),
        contentRiskIndicators: z.array(RiskIndicatorSchema).nullish(),
        pricingRiskIndicators: z.array(RiskIndicatorSchema).nullish(),
        websiteStructureRiskIndicators: z.array(RiskIndicatorSchema).nullish(),
        homePageScreenshotUrl: z.string().url().nullish(),
        ecosystem: z.array(EcosystemRecordSchema).nullish(),
        isAlert: z.boolean().nullish(),
        summary: z.string().nullish(),
        ongoingMonitoringSummary: z.string().nullish(),
        ongoingMonitoringChanges: z.array(OngoingMonitoringChangeSchema).nullish(),
        ongoingMonitoringChangesSnippet: z.string().nullish(),
        companyRegistryData: CompanyRegistryCheckResultSchema.nullish(),

        riskScore: z.coerce.number().nullish(),
        riskLevel: z.enum(MERCHANT_REPORT_RISK_LEVELS).nullish(),
        isWebsiteOffline: z.boolean().nullish(),
        allViolations: z
          .array(RiskIndicatorSchema.pick({ id: true, name: true, riskLevel: true, status: true }))
          .nullish(),
        highRiskVerticalAnalysisResults: z.array(HighRiskVerticalAnalysisResultSchema).nullish(),
        gatedContentCheck: z
          .object({
            status: z.string(),
            message: z.string().nullable(),
            sessionUrl: z.string().url().nullable(),
            screenshotBase64: z.string().nullable(),
          })
          .nullable()
          .optional(),
      })
      .passthrough()
      .nullable()
      .default({}),
  })
  .passthrough();
